/**
 * financing-taxonomy.js — Single source of truth for financing vocabulary.
 *
 * Shared by:
 *   · StepFinancing (Crédito del Proyecto · interest treatment is read-only derived from preset)
 *   · ExistingLiabilitiesPanel (Pasivos Existentes · interest treatment editable free)
 *
 * Backend mirrors:
 *   · engine/liabilities.py LiabilitiesEngine.REPAYMENT_MODES
 *   · engine/financing.py bridge_loan_mode int → preset id mapping (see PROJECT_LOAN_PRESETS below)
 *   · routes/liabilities_routes.py REPAYMENT_MODES / INTEREST_TREATMENTS
 *
 * S14 CI lock: any change here MUST be reflected on the backend (test will assert).
 */

// Repayment modes — shared between Project Loan and Existing Liabilities
export const REPAYMENT_MODES = Object.freeze({
  liberation_per_unit: 'Liberación por unidad entregada',
  fixed_monthly:       'Monto fijo mensual',
  fixed_amortization:  'Amortización fija',
  bullet:              'Bullet (capital 100% al vencimiento)',
  pct_of_sales:        '% del flujo de ventas (cobranza)',
  manual_schedule:     'Tabla manual (mes / monto)',
  cash_sweep:          'Barrido de caja (oportunista)',
  bullet_emulated:     'Bullet (un solo pago al vencimiento)',
  custom_schedule:     'Custom (calendario del banco)',
});

// Interest treatment — shared vocabulary; editable freely only in Pasivos Existentes.
export const INTEREST_TREATMENTS = Object.freeze({
  served_monthly: 'Servido mensual (sale de la caja del proyecto)',
  capitalized:    'Capitalizado al principal (se suma al saldo, paga al final)',
});

// Rate modes
export const RATE_MODES = Object.freeze({
  fixed:    'Tasa fija anual',
  floating: 'Tasa flotante (TIIE base + spread en bps)',
});

// Priority (only Pasivos Existentes uses this)
export const LIABILITY_PRIORITIES = Object.freeze({
  senior_to_equity: 'Senior al equity (default)',
  subordinated:     'Subordinado al equity',
});

/**
 * PROJECT_LOAN_PRESETS — preset id → {label, repayment, interest, bridge_loan_mode, hidden}
 *
 * The interest treatment is DERIVED (read-only) from the preset because the
 * `FinancingEngine` (bridge) acopla repago + interés en `bridge_loan_mode`. In
 * the engine:
 *   bridge_loan_mode = 0  →  cash_sweep  + capitalized       · LEGACY (oculto)
 *   bridge_loan_mode = 1  →  liberation_per_unit + served_monthly
 *   bridge_loan_mode = 2  →  fixed_amortization + served_monthly
 *   bridge_loan_mode = 4  →  bullet + served_monthly         · NUEVO v2.7.0
 *
 * Mode 3 ("Ministraciones por avance") IS GHOST in v2.5.0+ — engine pre-computa la
 * curva de avance pero NUNCA la consume. Comportamiento idéntico al modo 1. Decisión
 * de Arturo (2026-02, FASE 2 banderas): ocultar de la UI.
 *
 * Mode 0 ("Capitalizable barrido") ES MARCADO HIDDEN — no corresponde a ningún
 * producto bancario real en México. Decisión Arturo (2026-02 cleanup): mantener
 * en motor por retrocompatibilidad pero NO ofrecer en UI nueva.
 */
export const PROJECT_LOAN_PRESETS = Object.freeze([
  {
    id: 'puente_real_liberacion',
    label: 'Puente Real (liberación por unidad)',
    description: 'Línea autorizada con DISPOSICIÓN POR AVANCE DE OBRA CERTIFICADO (G8 v2.7.1). Anticipo opcional en mes 1 + ministraciones mensuales = curva de avance × credit_line, con lag de valuación. Interés servido mensual sobre saldos. Repago proporcional al entregar viviendas (liberation = credit_line / total_units).',
    bridge_loan_mode: 1,
    repayment: 'liberation_per_unit',
    interest: 'served_monthly',
  },
  {
    id: 'simple_amortizable',
    label: 'Crédito Simple Amortizable',
    description: 'Desembolso único en mes de disposición. Gracia opcional sólo intereses; tras gracia, cuota fija mensual de capital = credit_line / (term − grace). Interés servido mensual sobre saldos.',
    bridge_loan_mode: 2,
    repayment: 'fixed_amortization',
    interest: 'served_monthly',
  },
  {
    id: 'simple_bullet',
    label: 'Crédito Simple Bullet',
    description: 'Desembolso único en mes de disposición. Solo intereses servidos mensual sobre saldo durante todo el plazo. Capital 100% pagadero al vencimiento = disbursement + term − 1 (bullet).',
    bridge_loan_mode: 4,
    repayment: 'bullet',
    interest: 'served_monthly',
  },
  {
    id: 'custom_ministration',
    label: 'Puente Custom (calendario del banco)',
    description: 'Sprint 2 · v2.8.1. Capturas literalmente el calendario de ministraciones que te dio el banco (array de {mes, monto}) y el modo de repago que aplica (LPU, bullet, mixto o manual). Sin curva teórica ni lag automático — todo lo declaras tú. Ideal cuando el banco ya te entregó una tabla concreta.',
    bridge_loan_mode: 5,
    repayment: 'custom_schedule',
    interest: 'served_monthly',
  },
  {
    id: 'capitalizable_sweep',
    label: 'Capitalizable (barrido de caja) · legacy',
    description: 'LEGACY · Conservado para retro-compatibilidad de escenarios v2.5.0 y anteriores. Interés capitaliza al saldo y se hace barrido oportunista de cualquier excedente de caja. NO corresponde a un producto bancario mexicano estándar.',
    bridge_loan_mode: 0,
    repayment: 'cash_sweep',
    interest: 'capitalized',
    hidden: true,
  },
]);

export function presetFromBridgeMode(bridgeLoanMode) {
  return PROJECT_LOAN_PRESETS.find(p => p.bridge_loan_mode === Number(bridgeLoanMode))
      || PROJECT_LOAN_PRESETS[0];
}

/**
 * Bullet emulation helper for Pasivos Existentes — el engine no implementa
 * `bullet` explícito. Para emularlo, construimos un `manual_schedule` con UN solo
 * pago igual al principal en `start_month + duration_months − 1`.
 *
 * Devuelve el JSON string listo para guardar en manual_schedule_json.
 */
export function buildBulletSchedule(principal, startMonth, durationMonths) {
  const targetMonth = Math.max(1, Math.round(Number(startMonth) || 1) + Math.max(1, Math.round(Number(durationMonths) || 1)) - 1);
  const amount = Math.round((Number(principal) || 0) * 100) / 100;
  return JSON.stringify([{ month: targetMonth, amount }]);
}

// Subset of REPAYMENT_MODES that Pasivos Existentes actually offers (engine-backed + bullet emulation)
export const LIABILITY_REPAYMENT_OPTIONS = Object.freeze([
  ['liberation_per_unit', REPAYMENT_MODES.liberation_per_unit],
  ['fixed_monthly',       REPAYMENT_MODES.fixed_monthly],
  ['pct_of_sales',        REPAYMENT_MODES.pct_of_sales],
  ['manual_schedule',     REPAYMENT_MODES.manual_schedule],
  ['bullet_emulated',     REPAYMENT_MODES.bullet_emulated],
]);
